import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import type { UrlManualConfig, UrlManualItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  config: UrlManualConfig;
  maxCount: number;
  onChange: (next: UrlManualConfig) => void;
}

const EMPTY_ITEM: UrlManualItem = {
  title: '',
  url: '',
  imageUrl: null,
  summary: null,
  embed: null,
};

export function ConfigEditorUrlManual({ config, maxCount, onChange }: Props) {
  const update = (idx: number, patch: Partial<UrlManualItem>) => {
    const items = config.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...config, items });
  };
  const move = (idx: number, dir: -1 | 1) => {
    const items = [...config.items];
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target]!, items[idx]!];
    onChange({ ...config, items });
  };
  const remove = (idx: number) =>
    onChange({ ...config, items: config.items.filter((_, i) => i !== idx) });
  const add = () => {
    if (config.items.length >= maxCount) return;
    onChange({ ...config, items: [...config.items, { ...EMPTY_ITEM }] });
  };

  const remaining = maxCount - config.items.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {config.items.length} / {maxCount} รายการ
        </div>
        <Button type="button" variant="default" size="sm" disabled={remaining <= 0} onClick={add}>
          <Plus className="mr-1 h-4 w-4" /> เพิ่ม URL
        </Button>
      </div>

      {config.items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400 dark:border-slate-700">
          ยังไม่มี URL — กดปุ่ม "เพิ่ม URL" เพื่อเริ่ม
        </div>
      ) : (
        <ul className="space-y-3">
          {config.items.map((it, idx) => (
            <li
              key={idx}
              className="space-y-3 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs tabular-nums text-slate-400">รายการ #{idx + 1}</span>
                <div className="flex items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    title="เลื่อนขึ้น"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={idx === config.items.length - 1}
                    onClick={() => move(idx, 1)}
                    title="เลื่อนลง"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(idx)}
                    title="ลบ"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`url-title-${idx}`}>หัวข้อ *</Label>
                  <Input
                    id={`url-title-${idx}`}
                    value={it.title}
                    onChange={(e) => update(idx, { title: e.target.value })}
                    placeholder="ชื่อ link ที่จะแสดงบนการ์ด"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`url-url-${idx}`}>URL *</Label>
                  <Input
                    id={`url-url-${idx}`}
                    value={it.url}
                    onChange={(e) => update(idx, { url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`url-image-${idx}`}>รูปประกอบ (URL)</Label>
                  <Input
                    id={`url-image-${idx}`}
                    value={it.imageUrl ?? ''}
                    onChange={(e) => update(idx, { imageUrl: e.target.value || null })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`url-summary-${idx}`}>สรุปสั้น</Label>
                  <Input
                    id={`url-summary-${idx}`}
                    value={it.summary ?? ''}
                    onChange={(e) => update(idx, { summary: e.target.value || null })}
                    placeholder="ข้อความสั้นๆ บนการ์ด (ถ้ามี)"
                  />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor={`url-embed-${idx}`}>
                    Embed HTML (เช่น iframe code — ถ้าเป็น YouTube/Twitter)
                  </Label>
                  <textarea
                    id={`url-embed-${idx}`}
                    value={it.embed ?? ''}
                    onChange={(e) => update(idx, { embed: e.target.value || null })}
                    placeholder="<iframe ...></iframe> — ถ้ามี public site จะแสดง embed แทน link card"
                    rows={3}
                    className="border-input bg-background focus-visible:ring-ring ring-offset-background w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
